export type VictimStats = {
  appearances: number
  deathSentences: number
}

const STORAGE_KEY = 'trollilopolis:victimStats:v1'

/**
 * Persists per-victim kill stats in localStorage. The seed values from
 * constants act as a global baseline so the stats copy is funny on a
 * first-ever play; local plays accumulate on top of them.
 */
export class StatsService {
  private stats: Record<string, VictimStats> = {}

  constructor() {
    this.stats = this.load()
  }

  private load(): Record<string, VictimStats> {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return {}
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {}
      }
      // Keep only well-formed entries so a tampered or partial blob can't
      // leak NaN into the spoken stats.
      const clean: Record<string, VictimStats> = {}
      for (const [name, entry] of Object.entries(
        parsed as Record<string, unknown>,
      )) {
        const candidate = entry as Partial<VictimStats> | null
        if (
          candidate &&
          Number.isFinite(candidate.appearances) &&
          Number.isFinite(candidate.deathSentences)
        ) {
          clean[name] = {
            appearances: candidate.appearances as number,
            deathSentences: candidate.deathSentences as number,
          }
        }
      }
      return clean
    } catch (error) {
      console.warn('Could not load victim stats, starting fresh:', error)
    }
    return {}
  }

  private persist(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.stats))
    } catch (error) {
      console.warn('Could not persist victim stats:', error)
    }
  }

  public getStats(name: string, seed: VictimStats): VictimStats {
    const stored = this.stats[name]
    if (!stored) {
      return { ...seed }
    }
    return {
      appearances: seed.appearances + stored.appearances,
      deathSentences: seed.deathSentences + stored.deathSentences,
    }
  }

  public recordAppearance(name: string): void {
    const entry = this.stats[name] ?? { appearances: 0, deathSentences: 0 }
    entry.appearances++
    this.stats[name] = entry
    this.persist()
  }

  public recordDeathSentence(name: string): void {
    const entry = this.stats[name] ?? { appearances: 0, deathSentences: 0 }
    entry.deathSentences++
    this.stats[name] = entry
    this.persist()
  }
}
